import React, { useEffect, useContext, useState, useRef, useMemo } from "react";
import { SearchModel } from "types/qbank/searchModel";
import { Loader } from "components/ui";
import { useFilter } from "hooks/filter";
import { useApi } from "hooks/api";
import { useInfiniteQuery, useQuery } from "react-query";
import { Box, List, Typography } from "@mui/material";
import DetailDialog from "./detailModal/DetailDialog";
import { useMediaModal } from "hooks/mediaModal";
import MediaCard from "./MediaCard";
import MediaList from "./MediaList";
import { Media } from "types/qbank/media";
import { TotalHitsContext } from "../../hooks/TotalHits";

export default function SearchResult() {
  const { modal, setModal } = useMediaModal();
  const { search, getDeploymentSite } = useApi();
  const { searchState, contentView, gridSize } = useFilter();
  const totalHitsContext = useContext(TotalHitsContext);

  const _searchLimit = 40;

  /**
   * compute how many masonry columns to use based on current grid size
   * and window width. this keeps the layout stable even when new pages
   * are appended with fetchNextPage().
   */
  const getMasonryColumnCount = (size: string) => {
    const width = window.innerWidth;

    const breakpoints = {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
    };

    let key: "xs" | "sm" | "md" | "lg" = "xs";
    if (width >= breakpoints.lg) key = "lg";
    else if (width >= breakpoints.md) key = "md";
    else if (width >= breakpoints.sm) key = "sm";

    switch (size) {
      case "small":
        if (key === "lg") return 6;
        if (key === "md") return 5;
        if (key === "sm") return 4;
        return 4;
      case "medium":
        if (key === "lg") return 5;
        if (key === "md") return 4;
        if (key === "sm") return 3;
        return 3;
      case "large":
      default:
        if (key === "lg") return 4;
        if (key === "md") return 3;
        if (key === "sm") return 2;
        return 2;
    }
  };

  const [masonryColumns, setMasonryColumns] = useState<number>(() =>
    getMasonryColumnCount(gridSize)
  );

  const masonryContainerRef = useRef<HTMLDivElement>(null);
  const columnRefs = useRef<(HTMLDivElement | null)[]>([]);

  const getGridColumnSizes = (size: string) => {
    switch (size) {
      case 'small':
        return {
          xs: 'repeat(auto-fill, minmax(70px, 1fr))',
          sm: 'repeat(auto-fill, minmax(100px, 1fr))',
          md: 'repeat(auto-fill, minmax(120px, 1fr))',
          lg: 'repeat(auto-fill, minmax(140px, 1fr))',
        };
      case 'medium':
        return {
          xs: 'repeat(auto-fill, minmax(100px, 1fr))',
          sm: 'repeat(auto-fill, minmax(150px, 1fr))',
          md: 'repeat(auto-fill, minmax(180px, 1fr))',
          lg: 'repeat(auto-fill, minmax(200px, 1fr))',
        };
      case 'large':
      default:
        return {
          xs: 'repeat(auto-fill, minmax(120px, 1fr))',
          sm: 'repeat(auto-fill, minmax(200px, 1fr))',
          md: 'repeat(auto-fill, minmax(240px, 1fr))',
          lg: 'repeat(auto-fill, minmax(280px, 1fr))',
        };
    }
  };

  const deploymentSiteData = useQuery(
    ["getDeploymentSite", searchState.currentCategory.deploymentSiteId],
    () =>
      getDeploymentSite(parseInt(searchState.currentCategory.deploymentSiteId)),
    {
      staleTime: Infinity,
      retry: false,
    }
  );

  const handleDoubleClick = (media: Media, event: React.MouseEvent<HTMLDivElement>) => {
    setModal({ open: true, media: media })
  };

  const doSearch = ({ pageParam = 0 }: any) => {
    let searchParams: SearchModel = {
      limit: _searchLimit,
      offset: pageParam,
      ...searchState.search,
    };

    return search(searchParams);
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery(["search", searchState.search], doSearch, {
    retry: 0,
    staleTime: 50000,
    getNextPageParam: (lastPage, pages) => {
      return lastPage.limit + lastPage.offset;
    },
  });

  useEffect(() => {
    if (data !== undefined) {
      const _windowHeight = window.innerHeight * 1.5;
      const lastPage = data.pages[data.pages.length - 1];
      const hasMore = lastPage.offset + lastPage.limit < lastPage.totalHits;

      function handleScrollPopUp(e: Event) {
        const scrollElement = e.target as HTMLDivElement;
        if (
          scrollElement.scrollTop >
          scrollElement.offsetHeight - _windowHeight &&
          hasMore &&
          !isFetchingNextPage
        ) {
          fetchNextPage();
        }
      };

      const scrollableElement = document.querySelector(".connector");

      if (scrollableElement) {
          scrollableElement.addEventListener("scroll", handleScrollPopUp);
      }
      if (totalHitsContext) {
        totalHitsContext.setTotalHits(data.pages[0]?.totalHits);
      }
      return () => {
        if (scrollableElement) {
          scrollableElement.removeEventListener("scroll", handleScrollPopUp);
        }
      };
    }
  }, [data, fetchNextPage, hasNextPage, isFetchingNextPage, totalHitsContext]);

  // keep masonry column count in sync with window width and grid size
  useEffect(() => {
    if (contentView !== "masonry") {
      return;
    }

    const handleResize = () => {
      const newColumnCount = getMasonryColumnCount(gridSize);
      if (newColumnCount !== masonryColumns) {
        setMasonryColumns(newColumnCount);
        // reset column refs when column count changes
        columnRefs.current = Array(newColumnCount).fill(null);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [contentView, gridSize, masonryColumns]);

  // initialize column refs array when masonry columns change
  useEffect(() => {
    if (contentView === "masonry" && masonryColumns > 0) {
      columnRefs.current = Array(masonryColumns).fill(null);
    }
  }, [masonryColumns, contentView]);

  // Memoize the distribution of items across columns for masonry layout
  // This ensures we only recalculate when pages or column count changes
  const itemsByColumn = useMemo(() => {
    if (!data || contentView !== "masonry" || !masonryColumns || masonryColumns <= 0) {
      return [];
    }

    const columns: Media[][] = Array.from({ length: masonryColumns }, () => []);
    let totalIndex = 0;

    data.pages.forEach((gridPage) => {
      gridPage.results?.forEach((item) => {
        const columnIndex = totalIndex % masonryColumns;
        columns[columnIndex].push(item);
        totalIndex++;
      });
    });

    return columns;
  }, [data, masonryColumns, contentView]);

  if (
    isLoading ||
    isError ||
    !data ||
    deploymentSiteData.isLoading ||
    deploymentSiteData.isError ||
    !deploymentSiteData.data
  ) {
    return <Loader />;
  }

  const getTotalCount = () => {
    if (data && data.pages && data.pages.length > 0) {
      return data.pages[0].totalHits;
    }
    return 0;
  };
  return (
    <>
    <React.Fragment>
      {getTotalCount() === 0 ? (
            <Box mt={2} sx={{ flexGrow: 1 }}>
              <Typography variant="h4" textAlign="center">
                No results!
              </Typography>
            </Box>
          ) : (
            <>
              {contentView === "grid" || contentView === "masonry" ? (
                <Box
                  mt={2}
                  sx={{
                    flexGrow: 1,
                  }}
                >
                  {contentView === "masonry" ? (
                    <Box
                      ref={masonryContainerRef}
                      className="masonry-grid"
                      sx={{
                        display: "flex",
                        gap: "12px",
                        width: "100%",
                      }}
                    >
                      {masonryColumns > 0 && Array.from({ length: masonryColumns }).map((_, colIndex) => (
                        <Box
                          key={colIndex}
                          ref={(el: HTMLDivElement | null) => {
                            // Ensure the array is large enough
                            while (columnRefs.current.length <= colIndex) {
                              columnRefs.current.push(null);
                            }
                            columnRefs.current[colIndex] = el;
                          }}
                          sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            minWidth: 0,
                          }}
                        >
                          {(itemsByColumn[colIndex] || []).map((item) => (
                            <Box
                              key={item.mediaId}
                              className="masonry-item"
                              sx={{
                                marginBottom: "12px",
                                padding: "4px",
                                borderRadius: "4px",
                                backgroundColor: "rgba(0, 0, 0, 0.02)",
                                display: "block",
                                width: "100%",
                              }}
                            >
                              <MediaCard
                                media={item}
                                deploymentSite={deploymentSiteData.data}
                              />
                            </Box>
                          ))}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                          display: "grid",
                          gridTemplateColumns: getGridColumnSizes(gridSize),
                          gap: "8px",
                        }}
                      >
                        {data.pages.map((gridPage, i) =>
                          gridPage.results?.map((item) => (
                            <Box
                              key={item.mediaId}
                              sx={{
                                aspectRatio: "19 / 20",
                              }}
                            >
                              <MediaCard
                                key={item.mediaId}
                                media={item}
                                deploymentSite={deploymentSiteData.data}
                              />
                            </Box>
                          ))
                        )}
                    </Box>
                  )}
                </Box>
              ) : (
                <List
                  sx={{
                    width: "100%",
                    bgcolor: "background.paper",
                  }}
                >
                  {data.pages.map((listPage, i) => (
                    listPage.results?.map((item) => (
                    <MediaList
                      key={item.mediaId}
                      media={item}
                      deploymentSite={deploymentSiteData.data}
                    />
                      ))
                    ))}
                </List>
              )}
            </>
          )}
    </React.Fragment>

      {modal.open && <DetailDialog deploymentSite={deploymentSiteData.data} onDoubleClick={handleDoubleClick} />}
    </>
)
}
